import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import { Card } from "react-bootstrap";
import TableSearchInput from "app/components/TableSearchInput";
import TableEmptyState from "app/components/TableEmptyState";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getEppActivityTableData,
  selectAllEppActivityTableData,
  selectGetEppActivityTableDataStatus,
  selectDepartments,
} from "../slice";
import { selectCompanyInfo, selectTeamsByTeamId } from "app/containers/Global/slice";
import { selectChildCompanies } from "app/containers/Hierarchy/slice";
import SortableTable from "app/components/SortableTable";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import { trackCSVExport } from "utils/trackingFunctions";
import { TableHeader } from "app/components/SortableTable/types";
import {
  EppEntityType,
  EppUserActivityRow,
  EppEntityActivityRow,
  EppUserResponse,
} from "../types";
import { getCompanyName } from "../helpers";
import RecordDropdown from "app/components/Dropdowns/RecordDropdown";
import {
  EppEntityTypes,
  EPP_TRAIT_KEYS,
  EPP_USER_CSV_TABLE_HEADERS,
  EPP_ENTITY_CSV_TABLE_HEADERS,
} from "../constants";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

interface Props {
  companyAccountId?: string;
  companySelector?: ReactNode;
}

const WorkplaceInsightsTable = ({ companyAccountId, companySelector }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // ---------------------------- Selectors ----------------------------
  const allEppActivityTableData = useAppSelector(selectAllEppActivityTableData);
  const getEppActivityTableDataStatus = useAppSelector(
    selectGetEppActivityTableDataStatus
  );
  const companyInfo = useAppSelector(selectCompanyInfo);
  const childCompanies = useAppSelector(selectChildCompanies);
  const allTeamsById = useAppSelector(selectTeamsByTeamId);
  const departments = useAppSelector(selectDepartments);

  // ---------------------------- States ----------------------------
  const [entitySelected, setEntitySelected] = useState<EppEntityType>("user");
  const [displayedInput, setDisplayedInput] = useState<string>("");

  // ---------------------------- Effect ----------------------------
  useEffect(() => {
    dispatch(
      getEppActivityTableData({
        entityType: entitySelected,
        companyAccountId,
      })
    );
  }, [dispatch, entitySelected, companyAccountId]);

  const isTableLoading = getEppActivityTableDataStatus === "loading";

  // ---------------------------- Data Access ----------------------------
  const rawData = allEppActivityTableData?.[entitySelected];

  const userRows: EppUserActivityRow[] = useMemo(() => {
    if (entitySelected !== "user" || !rawData) return [];
    return (rawData as EppUserResponse).users ?? [];
  }, [entitySelected, rawData]);

  const entityRows: EppEntityActivityRow[] = useMemo(() => {
    if (entitySelected === "user" || !rawData) return [];
    return (rawData as EppEntityActivityRow[]) ?? [];
  }, [entitySelected, rawData]);

  // ---------------------------- Filtering ----------------------------
  const getFilteredUserRows = useCallback((): EppUserActivityRow[] => {
    if (!displayedInput) return userRows;
    const lowerInput = displayedInput.toLowerCase();
    return userRows.filter((row) => {
      const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`
        .trim()
        .toLowerCase();
      return fullName.includes(lowerInput);
    });
  }, [displayedInput, userRows]);

  const getFilteredEntityRows = useCallback((): EppEntityActivityRow[] => {
    if (!displayedInput) return entityRows;
    const lowerInput = displayedInput.toLowerCase();
    return entityRows.filter((row) =>
      (row.name ?? "").toLowerCase().includes(lowerInput)
    );
  }, [displayedInput, entityRows]);

  // ---------------------------- Navigation ----------------------------
  const onUserClick = (userAccountId: number) => {
    navigate(`/UserGuide/${userAccountId}?tab=Workplace+Insights`);
  };

  const onTeamClick = (teamId: number) => {
    navigate(`/TeamGuide/${teamId}?tab=Team+Personality`);
  };

  // ---------------------------- Table Row Builders ----------------------------
  const getUserTableRow = (row: EppUserActivityRow) => {
    const { userAccountId, firstName, lastName, traitScores, companyAccountId: rowCompanyId } = row;
    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    const isParentCompanyUser = !rowCompanyId || rowCompanyId === companyInfo?.companyAccountId;

    const traitCells: Record<
      string,
      { displayValue: string; sortValue: number }
    > = {};
    for (const trait of EPP_TRAIT_KEYS) {
      const value = traitScores?.[trait];
      traitCells[trait] = {
        displayValue: value != null ? String(value) : "-",
        sortValue: value ?? -1,
      };
    }

    return {
      name: {
        sortValue: fullName,
        displayValue: isParentCompanyUser ? (
          <button
            className="button-link"
            onClick={() => onUserClick(userAccountId)}
          >
            <p className="user-name-cell__name">{fullName}</p>
          </button>
        ) : (
          <p className="user-name-cell__name">{fullName}</p>
        ),
      },
      ...(companySelector
        ? { company: getCompanyName(rowCompanyId, companyInfo, childCompanies) }
        : {}),
      dateEventTaken: row.dateEventTaken
        ? new Date(row.dateEventTaken).toLocaleDateString("en-US")
        : "-",
      ...traitCells,
    };
  };

  const getEntityTableRow = (row: EppEntityActivityRow) => {
    const {
      id,
      name,
      totalMembers,
      membersWithEpp,
      completionRate,
      traitScores,
    } = row;

    const completionPercentage = Math.round(completionRate * 100);

    const traitCells: Record<
      string,
      { displayValue: string; sortValue: number }
    > = {};
    for (const trait of EPP_TRAIT_KEYS) {
      const value = traitScores?.[trait];
      traitCells[trait] = {
        displayValue: value != null ? String(value) : "-",
        sortValue: value ?? -1,
      };
    }

    const nameCell =
      entitySelected === "team" ? (
        <button className="button-link" onClick={() => onTeamClick(id)}>
          <p className="user-name-cell__name">{name}</p>
        </button>
      ) : (
        <p>{name}</p>
      );

    // The API names each row's company; the lookups below only know the current company's entities.
    const rowCompanyAccountId =
      row.companyAccountId ??
      (entitySelected === "team"
        ? allTeamsById[id]?.companyAccountId
        : departments[id]?.companyAccountId);

    return {
      name: {
        sortValue: name,
        displayValue: nameCell,
      },
      ...(companySelector
        ? {
            company: getCompanyName(rowCompanyAccountId, companyInfo, childCompanies),
          }
        : {}),
      membersWithEpp: membersWithEpp,
      totalMembers: totalMembers,
      completionRate: {
        sortValue: completionPercentage,
        displayValue: `${completionPercentage}% (${membersWithEpp}/${totalMembers})`,
      },
      ...traitCells,
    };
  };

  // ---------------------------- Headers ----------------------------
  const getHeaders = (): TableHeader[] => {
    const traitHeaders: TableHeader[] = EPP_TRAIT_KEYS.map((trait) => ({
      label: trait,
      key: trait,
    }));

    const companyHeader: TableHeader[] = companySelector
      ? [{ label: "Company", key: "company" }]
      : [];

    if (entitySelected === "user") {
      return [
        { label: "Name", key: "name" },
        ...companyHeader,
        { label: "Date Taken", key: "dateEventTaken", isDate: true },
        ...traitHeaders,
      ];
    }

    return [
      { label: "Name", key: "name" },
      ...companyHeader,
      { label: "Members with EPP", key: "membersWithEpp" },
      { label: "Total Members", key: "totalMembers" },
      { label: "Completion Rate", key: "completionRate", sortInverse: true },
      ...traitHeaders,
    ];
  };

  // ---------------------------- CSV ----------------------------
  const getCSVExportRows = useCallback(() => {
    trackCSVExport("Workplace Insights Activity");

    if (entitySelected === "user") {
      return getFilteredUserRows().map((row) => {
        const baseRow: Record<string, string> = {
          firstName: row.firstName ?? "",
          lastName: row.lastName ?? "",
          dateEventTaken: row.dateEventTaken
            ? new Date(row.dateEventTaken).toLocaleDateString("en-US")
            : "",
        };
        for (const trait of EPP_TRAIT_KEYS) {
          baseRow[trait] =
            row.traitScores?.[trait] != null
              ? String(row.traitScores[trait])
              : "";
        }
        return baseRow;
      });
    }

    return getFilteredEntityRows().map((row) => {
      const completionPercentage = Math.round(row.completionRate * 100);
      const baseRow: Record<string, string> = {
        name: row.name ?? "",
        membersWithEpp: String(row.membersWithEpp),
        totalMembers: String(row.totalMembers),
        completionRate: `${completionPercentage}%`,
      };
      for (const trait of EPP_TRAIT_KEYS) {
        baseRow[trait] =
          row.traitScores?.[trait] != null
            ? String(row.traitScores[trait])
            : "";
      }
      return baseRow;
    });
  }, [entitySelected, getFilteredUserRows, getFilteredEntityRows]);

  // ---------------------------- Search ----------------------------
  const getSearchPlaceholder = () => {
    switch (entitySelected) {
      case "team":
        return "Search for a team...";
      case "department":
        return "Search for a department...";
      default:
        return "Search for a user...";
    }
  };

  // ---------------------------- Render Sections ----------------------------
  const rows =
    entitySelected === "user"
      ? getFilteredUserRows().map(getUserTableRow)
      : getFilteredEntityRows().map(getEntityTableRow);

  const headers = getHeaders();

  const csvHeaders =
    entitySelected === "user"
      ? EPP_USER_CSV_TABLE_HEADERS
      : EPP_ENTITY_CSV_TABLE_HEADERS;

  const totalDataLength =
    entitySelected === "user" ? userRows.length : entityRows.length;

  return (
    <Card>
      <div className="d-flex justify-content-between align-items-center">
        <div className="column-gap-8px">
          <h2>Workplace Insights Activity</h2>
          <p>Showing EPP activity in this entire organization</p>
        </div>
        {companySelector}
      </div>
      {entitySelected !== "user" && (
        <div className="warning-banner lighter-blue flex">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1" />
          <div>
            <p className="fw-bold">
              Team and Department views show average EPP scores across all
              members who have completed the assessment.
            </p>
            <p>
              Switch to User view to see individual scores. Export CSV includes
              all dimensions for all users.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <TableSearchInput
          value={displayedInput}
          onChange={setDisplayedInput}
          placeholder={getSearchPlaceholder()}
          className="min-w-[300px]"
        />
        <div className="d-flex justify-content-between align-items-center flex-grow">
          <div className="row-gap-12px">
            <RecordDropdown
              record={EppEntityTypes}
              selected={entitySelected}
              onSelect={(e) => {
                setEntitySelected((e ?? "user") as EppEntityType);
                setDisplayedInput("");
              }}
              isDisabled={isTableLoading}
            />
          </div>
          <div>
            <ExportCSVButton
              headers={csvHeaders}
              getRows={getCSVExportRows}
              fileName="Workplace Insights Activity"
              disabled={isTableLoading}
            />
          </div>
        </div>
      </div>
      <SortableTable
        rows={rows}
        tableClassName="admin-console-table"
        columnHeaders={[...headers]}
        isLoading={isTableLoading}
      />
      <TableEmptyState
        isLoading={isTableLoading}
        totalDataCount={totalDataLength}
        filteredCount={rows.length}
        noDataTitle="No Workplace Insights activity found"
        noDataBody="Once there is Workplace Insights activity, it will be shown here."
        entitySelected={entitySelected}
        searchInput={displayedInput}
      />
    </Card>
  );
};

export default WorkplaceInsightsTable;
